<script setup lang="ts">
import { DronecastPlayer } from '@/sdk'
import { useDronecastStore } from '@/stores/dronecastStore'
import { useLocalInput } from '@/composables/useLocalStorage'
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import type { ConnectionState } from '@/sdk/core/errorMessages'
import { castroomIdEquals, type CastroomId } from '@dronecast/shared'
import PlayerStatusBar from '@/components/PlayerStatusBar.vue'
import { monitoringService } from '@/services/monitoring.service'
import { useMonitoringStore } from '@/stores/monitoring.store'
import { useAccessControl } from '@/composables/useAccessControl'
import { useRecordingControl } from '@/composables/useRecordingControl'
import type { LinkStatus } from '@dronecast/shared'

const route = useRoute()
const store = useDronecastStore()
const gcsId = useLocalInput('dronecast.gcsId', route.query.gcsId as string || 'KETI_GCS')
const selected = ref(-1)
const websocketUrl = computed(() => store.websocketUrl)

const player = ref<InstanceType<typeof DronecastPlayer> | null>(null)
const playerState = ref<ConnectionState>('idle')
const yoloEnabled = ref(false)

const { isRecording, start: startRecording, stop: stopRecording } = useRecordingControl()

const LAST_CASTROOM_KEY = 'dronecast.lastCastroomId'

const selectedCastroom = computed(() =>
  selected.value >= 0 ? store.castroomList[selected.value] : null
)

function saveLastCastroomId(id: CastroomId) {
  localStorage.setItem(LAST_CASTROOM_KEY, JSON.stringify(id))
}

function loadLastCastroomId(): CastroomId | null {
  const raw = localStorage.getItem(LAST_CASTROOM_KEY)
  if (!raw) return null
  try { return JSON.parse(raw) } catch { return null }
}

async function onClickFetch() {
  await store.fetchDronecastList(gcsId.value)
  if (store.castroomList.length === 0) {
    selected.value = -1
    return
  }
  const lastId = loadLastCastroomId()
  if (lastId) {
    const idx = store.castroomList.findIndex(c => castroomIdEquals(c.castingSource.castroomId, lastId))
    selected.value = idx >= 0 ? idx : 0
  } else {
    selected.value = 0
  }
}

const monitoringStore = useMonitoringStore()

const onConnectionStateChanged = (state: ConnectionState) => { playerState.value = state }

const onCastroomStatusChanged = (result: { castroomId: any; status: string; reason?: 'caster_stopped' | 'admin_closed' | 'error' }) => {
  // castroom 상태로 Caster↔Server Link 상태를 추정하여 반영
  const statusMap: Record<string, LinkStatus> = {
    casting: 'connected',
    ready: 'connected',
    waiting: 'unknown',
    closed: 'disconnected',
    stopped: 'disconnected',
  }
  const linkStatus = statusMap[result.status] || 'unknown'
  // BL-611: closed인 경우 reason별 메시지 분기
  let causeMessage: string | undefined
  if (result.status === 'closed') {
    if (result.reason === 'admin_closed') causeMessage = '관리자에 의해 중단되었습니다'
    else if (result.reason === 'error') causeMessage = '오류로 인해 종료되었습니다'
    else causeMessage = '송출이 종료되었습니다'
  }
  monitoringStore.updateCasterServerLink({
    status: linkStatus,
    causeMessage,
    cause: result.status === 'closed' ? 'media_flow' : undefined,
    updatedAt: Date.now(),
  })
}
const onWsStateChanged = (state: 'connecting' | 'connected' | 'reconnecting' | 'disconnected') => {
  monitoringService.updateSignalingState('player', state)
}
const onWebRtcStateChanged = (_role: 'viewer', connectionState?: string, iceState?: string) => {
  monitoringService.updateWebRtcState('player', connectionState, iceState)
}

const onCasterLinkStatusChanged = (status: string) => {
  const statusMap: Record<string, LinkStatus> = {
    connected: 'connected',
    unstable: 'unstable',
    disconnected: 'disconnected',
    unknown: 'unknown',
  }
  monitoringStore.updateCasterServerLink({
    status: statusMap[status] || 'unknown',
    causeMessage: status === 'unstable' ? '송출측 연결 불안정' : status === 'disconnected' ? '송출측 연결 끊김' : undefined,
    updatedAt: Date.now(),
  })
}

const onClickPlay = () => {
  if (!selectedCastroom.value) return
  player.value?.stop()
  // BL-613: 재연결 시 이전 castroom의 Caster↔Server Link 상태가 잔존하지 않도록 초기화
  monitoringService.resetRole('player')
  const id = selectedCastroom.value.castingSource.castroomId
  saveLastCastroomId(id)
  player.value?.play({ gcs: id.gcs, drone: id.drone, camera: id.camera })
}

const onClickStop = () => { player.value?.stop() }

const { canRecord, notifyLoginRequired } = useAccessControl()
const onClickRecording = () => {
  if (!canRecord.value) {
    notifyLoginRequired('녹화')
    return
  }
  if (isRecording.value) {
    stopRecording()
  } else if (selectedCastroom.value) {
    startRecording(selectedCastroom.value.castingSource.castroomId)
  }
}

const toggleYolo = () => { yoloEnabled.value = !yoloEnabled.value }
</script>

<template>
  <main>
    <h1 class="page-title">캐스트룸 영상 확인</h1>

    <div class="form-group horizontal">
      <label>GCS ID</label>
      <input v-model="gcsId" class="stream-select" />
      <button class="btn btn-primary" @click="onClickFetch">조회</button>

      <select v-model="selected" class="stream-select">
        <option :value="-1" disabled>스트림을 선택하세요</option>
        <option v-for="(castroom, index) in store.castroomList" :key="index" :value="index">
          {{ castroom.castingSource.droneName }} - {{ castroom.castingSource.cameraName }}
        </option>
      </select>

      <button class="btn btn-primary" @click="onClickPlay" :disabled="!selectedCastroom">재생</button>
      <button class="btn btn-secondary" @click="onClickStop" :disabled="playerState === 'idle' || playerState === 'stopped'">중지</button>
    </div>

    <div class="toolbar">
      <PlayerStatusBar />
      <div class="toolbar-buttons">
        <button
          class="btn btn-sm"
          :class="[isRecording ? 'btn-danger' : 'btn-secondary', { 'btn-locked': !canRecord }]"
          :disabled="canRecord && playerState !== 'playing'"
          :title="!canRecord ? '녹화 기능은 로그인 후 사용 가능합니다' : ''"
          @click="onClickRecording"
        >{{ isRecording ? '녹화 중지' : '녹화 시작' }}</button>
        <button class="btn btn-sm" :class="yoloEnabled ? 'btn-success' : 'btn-secondary'" @click="toggleYolo" :disabled="playerState !== 'playing'">YOLO {{ yoloEnabled ? 'ON' : 'OFF' }}</button>
        <span class="status-badge">{{ playerState }}</span>
      </div>
    </div>

    <div class="video-container">
      <DronecastPlayer
        ref="player"
        v-if="selectedCastroom"
        :castroomId="selectedCastroom.castingSource.castroomId"
        :wsUrl="websocketUrl"
        :detectionEnabled="yoloEnabled"
        @onCastroomStatusChanged="onCastroomStatusChanged"
        @onConnectionStateChanged="onConnectionStateChanged"
        @onWsStateChanged="onWsStateChanged"
        @onWebRtcStateChanged="onWebRtcStateChanged"
        @onCasterLinkStatusChanged="onCasterLinkStatusChanged"
      />
      <div v-else class="placeholder-message" style="padding:80px 0">캐스트룸을 조회하고 스트림을 선택하세요.</div>
    </div>

    <div class="status-legend">
      <span class="legend-item"><span class="dot idle"></span> 대기</span>
      <span class="legend-item"><span class="dot connecting"></span> 연결 중</span>
      <span class="legend-item"><span class="dot playing"></span> 재생 중</span>
      <span class="legend-item"><span class="dot stopped"></span> 중지됨</span>
      <span class="legend-item"><span class="dot error"></span> 오류</span>
      <span class="legend-label">현재: {{ playerState }}</span>
    </div>
  </main>
</template>
